import {
  V3_SET_PRODUCT_CONFIG,
  V3_CLEAR_PRODUCT_CONFIG,
  V3_ADD_TO_CART,
  V3_MARK_RETOUCHING_PROMPTED,
  V3_DECLINE_RETOUCHING,
  V3_DECLINE_INTERSTITIAL_PRODUCT,
  V3_DECLINE_PRE_CART_OFFER_PRODUCT,
  V3_MARK_PRE_CART_OFFER_PRODUCT_AS_PROMPTED,
  V3_STORE_CART,
  V3_STORE_CART_ERROR,
  V3_STORE_CART_SUCCESS,
  V3_SUBMITTING_CART,
  V3_SUBMIT_CART_SUCCESS,
  V3_SUBMIT_CART_ERROR,
  V3_CLEAR_CART,
  V3_REMOVE_CART_ITEM,
  V3_RESET_RETOUCHING,
  V3_UPDATE_SMS_NUMBER_CONFIRMED,
  V3_POP_ITEM_FROM_CONFIG,
  V3_REMOVE_ITEM_FROM_CONFIG,
  V3_SET_PERSONALIZATION_ON_ALL_ELIGIBLE_CART_ITEMS,
  V3_SKIP_PERSONALIZATION_ON_ALL_CART_ITEMS,
  V3_CHECKOUT_LOADING,
  V3_APPLY_DISCOUNT_CODE_TO_CART,
  V3_RESET_SUBMITTING_ORDER,
  V3_CONFIRM_SHIPPING_ADDRESS,
  V3_SET_ACCEPTED_PICS_PLUS,
  V3_SET_CART_UUID,
  V3_RESTORE_ABANDONED_CART_ATTRIBUTION,
  V3_SET_OPTIONAL_INCENTIVE_TO_CONFIGURE,
  V3_CLEAR_OPTIONAL_INCENTIVE_TO_CONFIGURE,
  V3_SKIP_OPTIONAL_INCENTIVE,
  V3_ADD_INCENTIVE_TO_ORDER,
} from '../../actions/v3Order';
import { GET_SHOOT_DATA } from '../../actions/action_shoot';
import cartUtils from '../../utils/cart';
import {
  clearReAddedOptionalIncentiveIgnores,
  handleAddToCart,
} from './handleAddToCart';
import { clearProductScopedSelections, nextSlotState } from './slotState';

const INITIAL_STATE = {
  acceptedPicsPlus: false,
  abandonedCartNotificationId: null,
  cartTotals: {
    total: 0,
    shipping: 0,
    tax: 0,
    discount: 0,
    familyDiscount: 0,
    grandTotal: 0,
  },
  checkoutIsLoading: false,
  discountCodes: [],
  offerPicsPlus: false,
  optionalCartLevelIncentiveSkusToIgnore: [],
  optionalIncentiveProductIdsToIgnoreByOrder: {},
  optionalIncentiveToConfigure: null,
  isPicsPlusMember: false,
  picsPlusSubscriptionInfo: null,
  picsPlusExperiment: null,
  subscriberCount: 0,
  offeredPicsPlus: false,
  orders: {},
  productConfig: {},
  shippingAddressConfirmed: false,
  smsNumberConfirmed: false,
  storingCart: false,
  submittingOrder: false,
  uuid: null,
};

// Adds a product to the optional-incentive ignore lists for its scope.
// Order-level declines are recorded against the order by product id;
// cart-level ones against the BENEFIT by SKU, because the same benefit is a
// different row on every price list (ENG-2043 R3). A row with no sku falls
// back to its id. Unknown scope lands on both lists so redirect suppression
// errs on the side of not re-prompting a bonus the parent just declined.
const appendOptionalIncentiveIgnores = ({
  cartLevelSkus,
  idsByOrder,
  productId,
  sku,
  incentiveAppliesTo,
  orderKey,
}) => {
  const isCartLevel = incentiveAppliesTo === 'cart_level';
  const isOrderLevel = incentiveAppliesTo === 'order_level';
  let nextCartLevelSkus = cartLevelSkus;
  let nextIdsByOrder = idsByOrder;

  if (isCartLevel || !isOrderLevel) {
    nextCartLevelSkus = [
      ...new Set([...cartLevelSkus, sku != null ? sku : productId]),
    ];
  }

  if ((isOrderLevel || !isCartLevel) && orderKey) {
    nextIdsByOrder = {
      ...idsByOrder,
      [orderKey]: [...new Set([...(idsByOrder[orderKey] || []), productId])],
    };
  }

  return { nextCartLevelSkus, nextIdsByOrder };
};

// An optional-incentive decline lives exactly as long as the order it was
// recorded against. When a removal drops an order out of `orders` (its last
// item went away), every decline recorded under that orderKey goes with it, so
// re-adding items under the same key (orderKey is `<eventId>-<studentId>`, so
// it comes back identical) prompts for the bonus again instead of being
// silently suppressed by a stale entry. When nothing is left in the cart at
// all, the cart-level declines reset too, matching V3_CLEAR_CART.
//
// Boundary, deliberately: an order that SURVIVES the removal keeps its
// decline, even if it drops back below the bonus threshold. Only emptying an
// order forgets what the parent declined for it.
const pruneOptionalIncentiveIgnoresForDroppedOrders = ({
  cartLevelSkus,
  idsByOrder,
  optionalIncentiveToConfigure,
  previousOrders,
  nextOrders,
}) => {
  const remainingOrders = nextOrders || {};
  const droppedOrderKeys = Object.keys(previousOrders || {}).filter(
    (key) => !remainingOrders[key],
  );

  if (droppedOrderKeys.length === 0) {
    return {
      nextCartLevelSkus: cartLevelSkus,
      nextIdsByOrder: idsByOrder,
      nextOptionalIncentiveToConfigure: optionalIncentiveToConfigure,
    };
  }

  const cartIsEmpty = Object.keys(remainingOrders).length === 0;
  let nextIdsByOrder;

  if (cartIsEmpty) {
    nextIdsByOrder = {};
  } else {
    // Copy before deleting: this map may still be the one held by the previous
    // state, which must not be mutated.
    nextIdsByOrder = { ...idsByOrder };
    droppedOrderKeys.forEach((key) => {
      delete nextIdsByOrder[key];
    });
  }

  const pendingOrderKey = optionalIncentiveToConfigure
    ? optionalIncentiveToConfigure.orderKey
    : null;
  const nextOptionalIncentiveToConfigure =
    cartIsEmpty || droppedOrderKeys.includes(pendingOrderKey)
      ? null
      : optionalIncentiveToConfigure;

  return {
    nextCartLevelSkus: cartIsEmpty ? [] : cartLevelSkus,
    nextIdsByOrder,
    nextOptionalIncentiveToConfigure,
  };
};

export default function (state = INITIAL_STATE, action) {
  Object.freeze(state);

  let existing;

  switch (action.type) {
    case V3_ADD_TO_CART:
      return handleAddToCart(state, action.payload);
    case 'V3_DECLINE_PERSONALIZATION': {
      const orderKey = action.payload.orderKey;
      return {
        ...state,
        orders: {
          ...state.orders,
          [orderKey]: {
            ...state.orders[orderKey],
            personalization: {
              ...state.orders[orderKey].personalization,
              shouldPrompt: false,
            },
          },
        },
      };
    }

    case 'V3_SUBMIT_PERSONALIZATION': {
      const orderKey = action.payload.orderKey;
      return {
        ...state,
        orders: {
          ...state.orders,
          [orderKey]: {
            ...state.orders[orderKey],
            personalization: {
              value: action.payload.value,
              shouldPrompt: false,
            },
          },
        },
      };
    }
    case V3_SET_PERSONALIZATION_ON_ALL_ELIGIBLE_CART_ITEMS: {
      const orderKey = action.payload.orderKey;
      const submittedPersonalizationValue = action.payload.personalizationValue;

      return {
        ...state,
        orders: {
          ...state.orders,
          [orderKey]: {
            ...state.orders[orderKey],
            cartItems: state.orders[orderKey].cartItems.map((item) => {
              if (
                item.personalizationStatus === 'personalization_optional' ||
                item.personalizationStatus === 'personalization_required'
              ) {
                return {
                  ...item,
                  personalizationValue: submittedPersonalizationValue,
                };
              }

              // Check childProducts
              if (item.children) {
                const updatedChildProducts = item.children.map((childItem) => {
                  if (
                    childItem.personalizationStatus ===
                      'personalization_optional' ||
                    childItem.personalizationStatus ===
                      'personalization_required'
                  ) {
                    return {
                      ...childItem,
                      personalizationValue: submittedPersonalizationValue,
                    };
                  }
                  return childItem;
                });
                return { ...item, children: updatedChildProducts };
              }

              return item;
            }),
            personalization: {
              value: submittedPersonalizationValue,
              shouldPrompt: false,
            },
          },
        },
      };
    }
    case V3_SKIP_PERSONALIZATION_ON_ALL_CART_ITEMS: {
      const orderKey = action.payload.orderKey;

      return {
        ...state,
        orders: {
          ...state.orders,
          [orderKey]: {
            ...state.orders[orderKey],
            cartItems: state.orders[orderKey].cartItems.map((item) => {
              if (
                item.personalizationStatus === 'personalization_optional' ||
                item.personalizationStatus === 'personalization_required'
              ) {
                return {
                  ...item,
                  personalizationValue: null,
                };
              }

              // Check childProducts
              if (item.children) {
                const updatedChildProducts = item.children.map((childItem) => {
                  if (
                    childItem.personalizationStatus ===
                      'personalization_optional' ||
                    childItem.personalizationStatus ===
                      'personalization_required'
                  ) {
                    return {
                      ...childItem,
                      personalizationValue: null,
                    };
                  }
                  return childItem;
                });
                return { ...item, children: updatedChildProducts };
              }

              return item;
            }),
            personalization: {
              value: null,
              shouldPrompt: false,
            },
          },
        },
      };
    }
    case V3_MARK_RETOUCHING_PROMPTED:
    case V3_DECLINE_RETOUCHING: {
      const { orderKey } = action.payload;
      const order = state.orders[orderKey];

      if (!order) {
        return state;
      }

      const updatedOrder = {
        ...order,
        retouching: {
          ...order.retouching,
          shouldPrompt: false,
        },
      };

      return {
        ...state,
        orders: {
          ...state.orders,
          [orderKey]: updatedOrder,
        },
      };
    }
    case V3_DECLINE_INTERSTITIAL_PRODUCT: {
      const { orderKey } = action.payload;
      const { productId } = action.payload;
      const order = state.orders[orderKey];

      if (!order) {
        return state;
      }

      const promptedProducts = order.interstitialProductsPrompted || [];
      const productAlreadyExists = promptedProducts.includes(productId);

      const updatedOrder = {
        ...order,
        interstitialProductsPrompted: productAlreadyExists
          ? promptedProducts
          : [...promptedProducts, productId],
      };

      return {
        ...state,
        orders: {
          ...state.orders,
          [orderKey]: updatedOrder,
        },
      };
    }
    case V3_DECLINE_PRE_CART_OFFER_PRODUCT:
    case V3_MARK_PRE_CART_OFFER_PRODUCT_AS_PROMPTED: {
      {
        const { orderKey } = action.payload;
        const { productId } = action.payload;
        const order = state.orders[orderKey];

        if (!order) {
          return state;
        }

        const promptedProducts = order.preCartOfferProductsPrompted || [];
        const productAlreadyExists = promptedProducts.includes(productId);

        const updatedOrder = {
          ...order,
          preCartOfferProductsPrompted: productAlreadyExists
            ? promptedProducts
            : [...promptedProducts, productId],
        };

        return {
          ...state,
          orders: {
            ...state.orders,
            [orderKey]: updatedOrder,
          },
        };
      }
    }
    case V3_SUBMIT_CART_ERROR: {
      return {
        ...state,
        submittingOrder: false,
      };
    }
    case V3_SUBMIT_CART_SUCCESS: {
      return {
        ...state,
        submittingOrder: false,
      };
    }
    case V3_SUBMITTING_CART: {
      return {
        ...state,
        submittingOrder: true,
      };
    }
    case V3_STORE_CART: {
      return {
        ...state,
        storingCart: true,
      };
    }
    case V3_STORE_CART_ERROR: {
      return {
        ...state,
        storingCart: false,
      };
    }
    case V3_STORE_CART_SUCCESS: {
      return {
        ...state,
        storingCart: false,
        cartTotals: action.payload.cartTotals,
        offerPicsPlus: action.payload.offerPicsPlus,
        isPicsPlusMember: action.payload.isPicsPlusMember || false,
        picsPlusSubscriptionInfo:
          action.payload.picsPlusSubscriptionInfo || null,
        picsPlusExperiment: action.payload.picsPlusExperiment || null,
        subscriberCount: action.payload.subscriberCount || 0,
      };
    }
    case V3_SET_PRODUCT_CONFIG: {
      const {
        index,
        isPrepay,
        itemToAdd,
        prepayRequiresSameValuePersonalization,
      } = action.payload;

      const itemToAddKey = Object.keys(itemToAdd)[0];

      let productConfig;

      // If the shoot is prepay and prepay requires the same value for personalization
      // across all cart items, ensure the config is updated with the same value for each
      // child product's personalization.
      if (
        itemToAddKey?.toLowerCase() === 'personalization' &&
        isPrepay &&
        prepayRequiresSameValuePersonalization &&
        itemToAdd.personalization !== undefined &&
        itemToAdd.personalization !== null &&
        itemToAdd.personalization !== ''
      ) {
        productConfig = Object.entries(state.productConfig).reduce(
          (newProductConfig, [key, value]) => {
            return {
              ...newProductConfig,
              [key]: {
                ...value,
                personalization:
                  value.personalization !== undefined &&
                  value.personalization !== null
                    ? itemToAdd.personalization
                    : value.personalization,
              },
            };
          },
          {},
        );
      } else {
        // If the conditions aren't met, just use the original productConfig
        productConfig = state.productConfig;
      }
      // nextSlotState is shared with addToConfig, which has to route on the same
      // answer this stores — see slotState.js for why they cannot derive it apart.
      return {
        ...state,
        productConfig: {
          ...productConfig,
          [index]: nextSlotState(productConfig[index], itemToAdd),
        },
      };
    }
    case V3_POP_ITEM_FROM_CONFIG: {
      const { index: popIndex, section } = action.payload;
      const slot = state.productConfig[popIndex];
      if (!slot || !slot[section]) return state;

      // Removing the PRODUCT takes its scoped selections with it. This is the
      // sequence the app actually performs: ConfigScreen's mount effect pops
      // `.product` when the customer reopens a configured slot's product screen
      // (configScreen.js), and only then do they pick the replacement. Leaving the
      // old background behind here is what put one on a child with no background
      // slot — counted by the completion gates, where it can even stand in for a
      // background another child still owes, and shipped to the lab by
      // buildCartObject.
      //
      // Re-picking the SAME product also loses these, which is correct: the flow
      // re-walks its steps after a product pick, so anything still needed is asked
      // for again.
      const remaining =
        section === 'product'
          ? clearProductScopedSelections(slot)
          : { ...slot };
      delete remaining[section];

      return {
        ...state,
        productConfig: { ...state.productConfig, [popIndex]: remaining },
      };
    }
    case V3_REMOVE_ITEM_FROM_CONFIG:
      existing = { ...state };
      const keys = Object.keys(
        existing.productConfig[action.payload.index],
      ).length;
      Object.keys(existing.productConfig).forEach((key, index) => {
        if (index < action.payload.index) return;
        else if (existing.productConfig[index + 1])
          existing.productConfig[index] = existing.productConfig[index + 1];
        else delete existing.productConfig[index];
      });
      action.payload.history.go(-keys);
      return existing;
    case V3_CLEAR_PRODUCT_CONFIG:
      let productConfig;

      if (action.payload) {
        productConfig = {
          ...state.productConfig,
          [action.payload]: [],
        };
      } else {
        productConfig = {};
      }

      return {
        ...state,
        productConfig,
      };
    case V3_CLEAR_CART: {
      return {
        ...state,
        cartTotals: {},
        discountCodes: [],
        optionalCartLevelIncentiveSkusToIgnore: [],
        optionalIncentiveProductIdsToIgnoreByOrder: {},
        optionalIncentiveToConfigure: null,
        orders: {},
        shippingAddressConfirmed: false,
        abandonedCartNotificationId: null,
        uuid: null,
      };
    }
    case V3_SET_CART_UUID: {
      if (state.uuid) return state;
      return {
        ...state,
        uuid: action.payload,
      };
    }
    case V3_RESTORE_ABANDONED_CART_ATTRIBUTION: {
      if (!action.payload.orders || !action.payload.uuid) return state;
      return {
        ...INITIAL_STATE,
        orders: action.payload.orders,
        discountCodes: action.payload.discountCodes || [],
        uuid: action.payload.uuid,
        abandonedCartNotificationId: action.payload.abandonedCartNotificationId,
      };
    }
    case GET_SHOOT_DATA: {
      // Refresh the cart-level incentive catalog stashed on this shoot's
      // orders (ENG-2965). The stash is persisted with the cart, so without
      // this a returning parent could be offered an incentive whose
      // threshold was raised or that was deactivated since the capture —
      // the freshly served catalog is authoritative whenever a shoot's
      // data loads.
      const shootData = action.payload?.data;
      const loadedEventId = shootData?.shoot?.event_id || shootData?.shoot?.id;
      if (!loadedEventId) return state;

      const tagRestriction = (product) => ({
        ...product,
        block_international_shipping:
          shootData.shoot?.block_international_shipping === true,
      });
      const freshCatalog = (shootData.incentive_products || [])
        .filter((product) => product.incentive_applies_to === 'cart_level')
        .map(tagRestriction);
      // Order-level rows are stashed separately (ENG-2043 R2) so an order
      // from a shoot that is not the active one can still be judged against
      // its own catalog.
      const freshOrderLevelCatalog = (shootData.incentive_products || [])
        .filter((product) => product.incentive_applies_to !== 'cart_level')
        .map(tagRestriction);
      // The owning company's opt-in flag, refreshed with the catalog: an
      // order must never keep a decline the studio no longer allows.
      const optionalIncentivesEnabled =
        shootData.shoot?.optional_incentives_enabled === true;

      const keyPrefix = `${loadedEventId}-`;
      // The catalog is gallery-specific within one shoot (buyer vs
      // non-buyer reorder price lists), so a same-shoot sibling order
      // backed by a DIFFERENT gallery must keep its own stash. Null on
      // either side (DP2 flows, legacy orders) keeps the event-level
      // refresh.
      const loadedGalleryId = shootData.pics_gallery_id ?? null;
      const orderKeysForShoot = Object.keys(state.orders).filter((key) => {
        if (!key.startsWith(keyPrefix)) return false;
        const orderGalleryId =
          state.orders[key].additionalData?.gallery_id ?? null;
        return (
          orderGalleryId === null ||
          loadedGalleryId === null ||
          orderGalleryId === loadedGalleryId
        );
      });
      if (orderKeysForShoot.length === 0) return state;

      const refreshedOrders = { ...state.orders };
      orderKeysForShoot.forEach((key) => {
        refreshedOrders[key] = {
          ...refreshedOrders[key],
          incentiveProducts: freshCatalog,
          orderLevelIncentiveProducts: freshOrderLevelCatalog,
          optionalIncentivesEnabled,
        };
      });

      // A bonus this company no longer lets parents decline must be
      // re-prompted, not silently suppressed by a decline recorded while it
      // was optional — so the refreshed orders' declines go with the flag
      // (ENG-2043 R2). Cart-level SKUs offered by this shoot's own catalog go
      // too: the shared-SKU rule would refuse to honour them anyway.
      if (optionalIncentivesEnabled) {
        return {
          ...state,
          orders: refreshedOrders,
        };
      }

      const nextIdsByOrder = {
        ...(state.optionalIncentiveProductIdsToIgnoreByOrder || {}),
      };
      orderKeysForShoot.forEach((key) => {
        delete nextIdsByOrder[key];
      });

      const mandatorySkus = new Set(
        freshCatalog.map((product) =>
          product.sku != null ? product.sku : product.id,
        ),
      );

      return {
        ...state,
        optionalCartLevelIncentiveSkusToIgnore: (
          state.optionalCartLevelIncentiveSkusToIgnore || []
        ).filter((sku) => !mandatorySkus.has(sku)),
        optionalIncentiveProductIdsToIgnoreByOrder: nextIdsByOrder,
        orders: refreshedOrders,
      };
    }
    case V3_REMOVE_CART_ITEM: {
      const { orderKey, cartItemId } = action.payload;
      const studentOrder = state.orders[orderKey];

      if (!studentOrder) {
        return state; // No changes, orderKey not found.
      }

      const cartItemToRemove = studentOrder.cartItems.find(
        (item) => item.id === cartItemId,
      );

      if (!cartItemToRemove) {
        return state; // No changes, cartItemId not found.
      }

      let newOptionalCartLevelIncentiveSkusToIgnore =
        state.optionalCartLevelIncentiveSkusToIgnore || [];
      let newOptionalIncentiveProductIdsToIgnoreByOrder =
        state.optionalIncentiveProductIdsToIgnoreByOrder || {};

      // A removal the PARENT made is a decline; one the app made for them
      // (the cart's restricted-shipping cleanup) is not, and must never be
      // recorded as one — see ENG-2043 R6.
      const recordDecline = action.payload.recordDecline !== false;

      if (cartItemToRemove.isIncentive && recordDecline) {
        ({
          nextCartLevelSkus: newOptionalCartLevelIncentiveSkusToIgnore,
          nextIdsByOrder: newOptionalIncentiveProductIdsToIgnoreByOrder,
        } = appendOptionalIncentiveIgnores({
          cartLevelSkus: newOptionalCartLevelIncentiveSkusToIgnore,
          idsByOrder: newOptionalIncentiveProductIdsToIgnoreByOrder,
          productId: cartItemToRemove.productId,
          sku: cartItemToRemove.sku ?? null,
          incentiveAppliesTo:
            cartItemToRemove.incentiveAppliesTo ||
            cartItemToRemove.incentive_applies_to,
          orderKey,
        }));
      }

      let updatedCartItems = studentOrder.cartItems;

      if (cartItemToRemove.isPersonalization) {
        // Iterate through all other items in the order and set personalizationValue to null,
        // also handle the child items if they exist.
        updatedCartItems = studentOrder.cartItems.map((item) => ({
          ...item,
          personalizationValue: null,
          children: item.children
            ? item.children.map((childItem) => ({
                ...childItem,
                personalizationValue: null,
              }))
            : item.children,
        }));
      }

      // TODO: seems like we can remove this, but need to test
      const newCartItemIds = studentOrder.cartItemIds.filter(
        (id) => id !== cartItemId,
      );

      const newCartItems = updatedCartItems.filter(
        (item) => item.id !== cartItemId,
      );

      // Remove the productId from interstitialProductsPrompted if it exists.
      const newInterstitialProductsPrompted =
        studentOrder.interstitialProductsPrompted.filter(
          (id) => id !== cartItemToRemove.productId,
        );

      // Re-qualify the cart's incentive items against the totals the
      // removal leaves behind (ENG-2965). The full policy — quantity /
      // product-option / BYO-aware totals, per incentive level, voucher
      // value, self-exclusion, the sibling-order sweep — lives in
      // cartUtils.ordersWithUnearnedIncentivesRemoved so the quantity
      // steppers (ENG-2967) can apply the identical rules when a
      // decrement lowers spend. Vouchers live in the cartVoucherItems
      // slice, which this reducer cannot read; postConfigRoutingMiddleware
      // injects the subtotal into the action payload. Absent (e.g. a
      // direct dispatch in a test) it is 0.
      const editedOrders = {
        ...state.orders,
        [orderKey]: {
          ...studentOrder,
          cartItemIds: newCartItemIds,
          cartItems: newCartItems,
          interstitialProductsPrompted: newInterstitialProductsPrompted,
        },
      };

      const newOrders = cartUtils.ordersWithUnearnedIncentivesRemoved(
        editedOrders,
        orderKey,
        action.payload.voucherTotalCents,
      );

      // Declines recorded against an order that no longer exists must not
      // outlive it. Derived from the final `newOrders` map — which the
      // re-qualification above may have emptied, along with any sibling
      // order it swept — rather than from a single deletion site, so it
      // holds however the order set was computed.
      const {
        nextCartLevelSkus: prunedCartLevelIncentiveSkusToIgnore,
        nextIdsByOrder: prunedIncentiveIdsToIgnoreByOrder,
        nextOptionalIncentiveToConfigure,
      } = pruneOptionalIncentiveIgnoresForDroppedOrders({
        cartLevelSkus: newOptionalCartLevelIncentiveSkusToIgnore,
        idsByOrder: newOptionalIncentiveProductIdsToIgnoreByOrder,
        optionalIncentiveToConfigure: state.optionalIncentiveToConfigure,
        previousOrders: state.orders,
        nextOrders: newOrders,
      });

      return {
        ...state,
        optionalCartLevelIncentiveSkusToIgnore:
          prunedCartLevelIncentiveSkusToIgnore,
        optionalIncentiveProductIdsToIgnoreByOrder:
          prunedIncentiveIdsToIgnoreByOrder,
        optionalIncentiveToConfigure: nextOptionalIncentiveToConfigure,
        orders: newOrders,
      };
    }
    // A bonus put straight back into the order that earned it, with no trip
    // through the configurator (ENG-2043 re-add Tier 1). The cart object was
    // built by the addIncentiveToOrder thunk from the TARGET order, so this
    // case only has to place it: variant id against that order's own items,
    // and the decline it answers cleared under that order's key.
    case V3_ADD_INCENTIVE_TO_ORDER: {
      const { orderKey, cartObject, productId, sku } = action.payload;
      const order = state.orders[orderKey];

      if (!order) {
        return state;
      }

      const cartItems = order.cartItems || [];
      const newCartItem = cartUtils.createNewCartItem(
        cartObject,
        cartUtils.getNextVariantId(cartItems, cartObject.productId),
      );

      return {
        ...state,
        ...clearReAddedOptionalIncentiveIgnores(state, {
          productId,
          sku,
          orderKey,
        }),
        optionalIncentiveToConfigure:
          cartUtils.optionalIncentiveMarkerMatchesProduct(
            state.optionalIncentiveToConfigure,
            {
              productId,
              sku,
              incentiveAppliesTo: cartObject.incentiveAppliesTo,
            },
          )
            ? null
            : state.optionalIncentiveToConfigure,
        orders: {
          ...state.orders,
          [orderKey]: {
            ...order,
            cartItemIds: [...(order.cartItemIds || []), newCartItem.id],
            cartItems: [...cartItems, newCartItem],
          },
        },
      };
    }
    case V3_SET_OPTIONAL_INCENTIVE_TO_CONFIGURE: {
      const { productId, sku, orderKey } = action.payload;
      return {
        ...state,
        optionalIncentiveToConfigure: { productId, sku: sku ?? null, orderKey },
      };
    }
    case V3_SKIP_OPTIONAL_INCENTIVE: {
      const { productId, sku, incentiveAppliesTo, orderKey } = action.payload;
      const { nextCartLevelSkus, nextIdsByOrder } =
        appendOptionalIncentiveIgnores({
          cartLevelSkus: state.optionalCartLevelIncentiveSkusToIgnore || [],
          idsByOrder: state.optionalIncentiveProductIdsToIgnoreByOrder || {},
          productId,
          sku: sku ?? null,
          incentiveAppliesTo,
          orderKey,
        });
      return {
        ...state,
        optionalCartLevelIncentiveSkusToIgnore: nextCartLevelSkus,
        optionalIncentiveProductIdsToIgnoreByOrder: nextIdsByOrder,
      };
    }
    case V3_CLEAR_OPTIONAL_INCENTIVE_TO_CONFIGURE:
      return { ...state, optionalIncentiveToConfigure: null };
    case V3_RESET_RETOUCHING: {
      const { orderKey } = action.payload;
      const studentOrder = state.orders[orderKey];

      if (!studentOrder) {
        return state; // No changes, orderKey not found.
      }

      const newRetouching = studentOrder.retouching;
      newRetouching.shouldPrompt = true;

      const newStudentOrder = {
        ...studentOrder,
        retouching: newRetouching,
      };

      const newOrders = { ...state.orders, [orderKey]: newStudentOrder };

      return {
        ...state,
        orders: newOrders,
      };
    }
    case V3_UPDATE_SMS_NUMBER_CONFIRMED: {
      return {
        ...state,
        smsNumberConfirmed: action.payload,
      };
    }
    case V3_CHECKOUT_LOADING: {
      const { checkoutIsLoading } = action.payload;

      return {
        ...state,
        checkoutIsLoading,
      };
    }
    case V3_APPLY_DISCOUNT_CODE_TO_CART: {
      return cartUtils.applyDiscountCodeToCart(state, action.payload.data);
    }
    case V3_RESET_SUBMITTING_ORDER: {
      return {
        ...state,
        submittingOrder: false,
      };
    }
    case V3_CONFIRM_SHIPPING_ADDRESS: {
      return {
        ...state,
        shippingAddressConfirmed: true,
      };
    }
    case V3_SET_ACCEPTED_PICS_PLUS: {
      const { accepted } = action.payload;
      return {
        ...state,
        acceptedPicsPlus: accepted,
        offeredPicsPlus: true,
      };
    }
    default:
      return state;
  }
}
